import React, { useState } from 'react';
import { Avatar, styled, Typography } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import SidebarItem from './SidebarItem';
import CustomNavLink from './CustomNavLink';
import { useApp } from 'hooks/app';
import { SIDEBAR_WIDTH } from 'constants/constants';
import { useSelector } from 'store/selector';
import { SidebarProvider } from './hook/useSidebar';
import { DashboardOutlined, Home, InsightsOutlined } from '@mui/icons-material';
import Settings from './collapsible/Settings';
import UserControlSidebar from './user/UserControlSidebar';
import Registrations from './collapsible/Registrations';
import Surgeries from './collapsible/Surgeries';
import { usePermissionRules } from 'hooks/permissionRules';
import Ambulatory from './collapsible/Ambulatory';

const CustomDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    display: 'flex',
    justifyContent: 'space-between',
    width: SIDEBAR_WIDTH,
    padding: theme.spacing(1.25),
    // base branca sólida sob o degradê: na gaveta temporária (mobile) o conteúdo da página não pode aparecer por trás
    background:
      'linear-gradient(180deg, rgba(132, 20, 73, 0.14) 0%, rgba(255, 247, 251, 0.96) 28%, #ffffff 100%), #ffffff !important',
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: '8px 0 24px rgba(15, 23, 42, 0.06)',
    '@media print': {
      display: 'none',
    },
  },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  minHeight: 54,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const BrandMark = styled('div')(({ theme }) => ({
  width: 34,
  height: 34,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 800,
  fontSize: 15,
}));

const CustomAvatar = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: 12,
  fontWeight: 700,
}));

const Content = styled('div')(({ theme }) => ({
  transition: 'opacity 0.4s ease',
  opacity: 1,
  overflowY: 'auto',
  paddingRight: theme.spacing(0.25),
}));

const AccountLink = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const AccountCard = styled(CustomNavLink)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr)',
  alignItems: 'center',
  gap: theme.spacing(1),
  minHeight: 52,
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  color: theme.palette.text.primary,
  textDecoration: 'none',
  transition: 'background-color 0.2s ease, transform 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
    transform: 'translateX(2px)',
  },
  '&.active': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '& .profile, & .name': {
      color: theme.palette.primary.contrastText,
    },
    '& .MuiAvatar-root': {
      backgroundColor: theme.palette.primary.contrastText,
      color: theme.palette.primary.main,
    },
  },
}));

const AccountInfo = styled('div')({
  minWidth: 0,
});

export type Collapsible = {
  settings: boolean;
  registrations: boolean;
  surgeries: boolean;
  ambulatory: boolean;
};

const Sidebar: React.FC = () => {
  const user = useSelector(state => state.user);
  const [shownUserControl, setShownUserControl] = useState(false);
  const { checkPermission } = usePermissionRules();
  const app = useApp();
  const [collapsible, setCollapsible] = useState<Collapsible>({
    settings: false,
    registrations: false,
    surgeries: false,
    ambulatory: false,
  });

  function handleCollapseClick(index: keyof Collapsible, closeOthers = true): void {
    if (closeOthers) {
      const keys = Object.keys(collapsible);

      let otherValues: Collapsible = {} as Collapsible;
      keys.forEach(key => {
        otherValues = {
          ...otherValues,
          [key]: false,
        };
      });

      setCollapsible({
        ...otherValues,
        [index]: !collapsible[index],
      });
      return;
    }

    setCollapsible({
      ...collapsible,
      [index]: !collapsible[index],
    });
  }

  const profileName = user?.formattedProfileName || user?.user_profile?.name || user?.role || 'Sem perfil';

  return (
    <CustomDrawer
      variant={app.isMobile || app.windowWidth < 1280 ? 'temporary' : 'persistent'}
      anchor="left"
      open={app.isOpenedMenu}
      onClose={app.handleOpenMenu}
    >
      <Content>
        <DrawerHeader>
          <BrandMark>UP</BrandMark>
          <div>
            <Typography variant="subtitle2" fontWeight={800}>
              Universo Plástica
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Admin
            </Typography>
          </div>
        </DrawerHeader>

        {user && (
          <SidebarProvider
            value={{
              handleCollapseClick,
              collapsible,
              setShownUserControl,
            }}
          >
            <SidebarItem to="/" icon={<Home />} text="Início" />
            {checkPermission('executive-dashboard') && (
              <SidebarItem to="/executive-dashboard" icon={<DashboardOutlined />} text="Dashboard" />
            )}
            {checkPermission('ambulatory') && <Ambulatory />}

            {checkPermission('registrations') && <Registrations />}

            {checkPermission('surgeries') && <Surgeries />}

            {checkPermission('reports') && <SidebarItem to="/reports" icon={<InsightsOutlined />} text="Relatórios" />}

            {checkPermission('settings') && <Settings />}

            {checkPermission('settings.user-control') && (
              <UserControlSidebar handleClose={() => setShownUserControl(false)} show={shownUserControl} />
            )}
          </SidebarProvider>
        )}
      </Content>

      <AccountLink>
        <AccountCard to="/account">
          {user ? <CustomAvatar>{user.name[0]}</CustomAvatar> : <CustomAvatar>U</CustomAvatar>}
          <AccountInfo>
            <Typography className="name" variant="body2" fontWeight={800} noWrap>
              {user ? user.name : 'Carregando...'}
            </Typography>
            <Typography className="profile" variant="caption" color="textSecondary" noWrap>
              {user ? profileName : 'Perfil'}
            </Typography>
          </AccountInfo>
        </AccountCard>
      </AccountLink>
    </CustomDrawer>
  );
};

export default Sidebar;
